/**
 * «Цены закупки» — подорожание по истории закупок: последняя закупка
 * товара против предыдущей у того же поставщика в той же единице, цена за
 * единицу без НДС. Та же цена, что сравнивает проверка П9 при приёмке;
 * порог — её же настройка. Период и «показать все» — в адресе.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Link, navigate, useRoute } from '../../router';
import { isoDate } from '../../ui';
import { stockApi } from './api';
import type { Prices as Data } from './api';

const PERIODS = [
  { days: 30, title: 'Месяц' },
  { days: 90, title: 'Квартал' },
  { days: 180, title: 'Полгода' },
];

export function Prices() {
  const route = useRoute();
  const days = Number(route.params.get('days')) || 180;
  const all = route.params.get('all') === 'true';
  const set = (next: { days?: number; all?: boolean }) => {
    const value = { days, all, ...next };
    const params = new URLSearchParams();
    if (value.days !== 180) params.set('days', String(value.days));
    if (value.all) params.set('all', 'true');
    const text = params.toString();
    navigate(text ? `/stock/prices?${text}` : '/stock/prices', { replace: true });
  };

  const query = useQuery<Data, ApiError>({
    queryKey: ['stock', 'prices', days, all],
    queryFn: () => stockApi.prices(days, all),
    refetchOnWindowFocus: true,
  });

  return (
    <div className="stock-stack">
      <div className="stock-filters">
        <div className="chips" role="tablist" aria-label="Период">
          {PERIODS.map((item) => (
            <button
              key={item.days}
              type="button"
              role="tab"
              aria-selected={item.days === days}
              className={`chip${item.days === days ? ' chip--active' : ''}`}
              onClick={() => set({ days: item.days })}
            >
              {item.title}
            </button>
          ))}
        </div>
        <label className="stock-check">
          <input type="checkbox" checked={all} onChange={(event) => set({ all: event.target.checked })} />
          Любое подорожание, не только выше порога
        </label>
      </div>

      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <PriceList data={query.data} />
      )}
    </div>
  );
}

function PriceList({ data }: { data: Data }) {
  return (
    <>
      <p className="stock-hint">
        С {isoDate(data.since)}: сравнено {data.compared} товаров у поставщиков.{' '}
        {data.all
          ? `Подорожало: ${data.count}.`
          : `Подорожало больше чем на ${data.threshold_pct}%: ${data.count}.`}{' '}
        Цена — за единицу, без НДС.
      </p>
      {data.items.length === 0 ? (
        <Empty
          title="Подорожания нет"
          text={
            data.compared === 0
              ? 'Сравнивать не с чем: строки накладных за этот период ещё не прочитаны или у каждого товара одна закупка.'
              : 'Цены у тех же поставщиков не выросли.'
          }
        />
      ) : (
        <section className="stock-card" aria-label="Подорожание">
          <ul className="stock-list">
            {data.items.map((row) => (
              <li key={`${row.goods_id}:${row.supplier.id}:${row.unit ?? ''}`} className="stock-row stock-row--split">
                <span className="stock-row__main">
                  <span className="stock-row__title">
                    {row.goods ?? 'товар'}
                    {row.unit ? `, ${row.unit}` : ''}
                  </span>
                  <span className="faint">
                    {row.supplier.name} · было {rub(row.previous.price)} ({isoDate(row.previous.date)}) →{' '}
                    <Link to={`/payments/invoices/${row.last.invoice_id}`}>
                      {rub(row.last.price)} ({isoDate(row.last.date)})
                    </Link>
                  </span>
                </span>
                <span className={`num stock-pct${row.above_threshold ? ' stock-negative' : ''}`}>
                  +{row.change_pct.replace('.', ',')}%
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
